import { motion } from 'framer-motion';
import { Shield, Lock, Globe, Users, Award, CheckCircle } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-teal-950/20 to-slate-900 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <div className="flex justify-center mb-6">
            <div className="relative">
              <img
                src="/pwa-192x192.png"
                alt="Agos Logo"
                className="h-20 w-20 object-contain hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-teal-500/20 blur-xl hover:bg-teal-400/30 transition-all duration-500" />
            </div>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Privacy is a <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Human Right</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Agos is building the next generation of identity verification.
            Decentralized, secure, and built with a "Privacy-First" architecture that puts you in control.
          </p>
        </motion.div>

        {/* Mission & Vision Grid */}
        <div className="grid md:grid-cols-2 gap-12 mb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 rounded-2xl border border-slate-700/50 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Shield className="w-32 h-32 text-teal-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-teal-400" />
              Our Mission
            </h2>
            <p className="text-slate-300 leading-relaxed text-lg">
              To provide a seamless, secure, and decentralized alternative to traditional centralized KYC systems.
              We believe that verifying your identity shouldn't mean sacrificing your privacy or security to a single point of failure.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 rounded-2xl border border-slate-700/50 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Globe className="w-32 h-32 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Globe className="w-6 h-6 text-emerald-400" />
              Our Vision
            </h2>
            <p className="text-slate-300 leading-relaxed text-lg">
              A world where digital identity is universally accessible, fraud-resistant, and owned by the individual.
              We are setting the new standard for trust in the Philippine digital economy and beyond.
            </p>
          </motion.div>
        </div>

        {/* Founder Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="glass-card bg-slate-800/40 p-12 rounded-3xl border border-slate-700/50">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="w-full md:w-1/3 flex justify-center">
                <div className="relative w-64 h-64 rounded-full overflow-hidden border-4 border-slate-700/50 shadow-2xl">
                  <img
                    src="/MFTFischer.png"
                    alt="Maria Fe Fischer"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="w-full md:w-2/3">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 rounded-full bg-teal-500/20 text-teal-400 text-sm font-semibold border border-teal-500/30">Founder & Lead Engineer</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Maria Fe Fischer</h2>
                <div className="space-y-4 text-slate-300 text-lg leading-relaxed">
                  <p>
                    As a Software Engineer with specialist training in Germany, Maria Fe brings rigorous European standards of
                    engineering and data privacy to the Philippine market.
                  </p>
                  <p>
                    Her expertise focuses on building high-assurance identity systems that do not compromise on user experience.
                    Frustrated by the inefficiencies and security risks of traditional "submit-and-pray" verification methods,
                    she founded Agos to bring <span className="text-white font-medium">Real-Time, AI-Powered, and Privacy-Preserving</span> verification to everyone.
                  </p>
                  <p>
                    She leads the technical architecture of Agos, ensuring that every line of code adheres to strict security protocols
                    and local compliance standards (PhilSys / Data Privacy Act of 2012).
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>European Engineering Standards</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-slate-400">
                    <CheckCircle className="w-5 h-5 text-teal-400" />
                    <span>Privacy-First Architecture</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Why Trust Agos? */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-12">Why Trust Agos?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ y: -5 }}
              className="glass-card p-8 rounded-xl border border-slate-700/50 text-left"
            >
              <div className="w-12 h-12 rounded-lg bg-teal-500/20 flex items-center justify-center mb-6">
                <Lock className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Military-Grade Security</h3>
              <p className="text-slate-400">
                We use AES-256 encryption for data at rest and TLS 1.3 for data in transit. Your identity data is never shared without your explicit consent.
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="glass-card p-8 rounded-xl border border-slate-700/50 text-left"
            >
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Hybrid AI Verification</h3>
              <p className="text-slate-400">
                Combining Google's Cloud Vision AI with specialized Tesseract OCR engines to ensure 99.9% accuracy, even with difficult Philippine ID formats.
              </p>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="glass-card p-8 rounded-xl border border-slate-700/50 text-left"
            >
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center mb-6">
                <Award className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Compliance Ready</h3>
              <p className="text-slate-400">
                Built to comply with the Data Privacy Act of 2012 (Republic Act 10173) and aligned with global GDPR standards for maximum protection.
              </p>
            </motion.div>
          </div>
        </div>

      </div>
    </div>
  );
}
